import React, { useMemo } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer, ComposedChart } from 'recharts';
import { AlertTriangle, CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const METER_COLORS = ['#3b82f6', '#f97316', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6'];

const PredictiveChart = ({ data, metersSummary }) => {
  const { t } = useTranslation();
  
  // 🎯 الإصلاح الثاني: تحويل التواريخ لقيم رقمية (Timestamps) لتفعيل مقياس الزمن الحقيقي
  const chartData = useMemo(() => {
    if (!data) return [];
    return data.map(item => ({
      ...item,
      timestamp: new Date(item.date).getTime()
    }));
  }, [data]);

  if (!data || data.length === 0 || !metersSummary) return null;

  const isAnyCritical = metersSummary.some(m => m.is_critical);

  return (
    <div className="w-full bg-white p-6 rounded-xl border border-slate-200 shadow-sm mt-4">
      <div className="flex flex-col md:flex-row justify-between items-start mb-6 border-b pb-4 gap-4">
        <div>
          <h4 className="text-sm font-black text-slate-800 uppercase flex items-center gap-2">
            <AlertTriangle size={18} className={isAnyCritical ? "text-red-600 animate-pulse" : "text-orange-500"} />
            {t('assets.ai.predictive_trajectory', 'Combined Predictive Trajectory')}
          </h4>
          <p className="text-[10px] text-slate-400 font-bold mt-1 uppercase tracking-widest">
            Multi-Sensor Linear Regression
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {metersSummary.map((m, idx) => (
            <div key={m.meter_num} className={`border px-3 py-2 rounded-lg text-center shadow-inner min-w-[120px] ${m.is_critical ? 'bg-red-600 border-red-800' : 'bg-slate-50 border-slate-200'}`}>
              <p className={`text-[9px] font-black uppercase tracking-widest mb-1`} style={{ color: m.is_critical ? 'white' : METER_COLORS[idx % METER_COLORS.length] }}>
                {m.meter_num}
              </p>
              <div className={`flex items-center justify-center gap-1 font-black ${m.is_critical ? 'text-white' : 'text-slate-700'}`}>
                {m.is_critical ? <AlertTriangle size={14}/> : <CalendarClock size={14} />}
                <span className="text-sm">
                  {m.is_critical ? "CRITICAL!" : `${m.rul_days} Days`}
                </span>
              </div>
              <p className={`text-[8px] font-bold mt-1 ${m.is_critical ? 'text-red-200' : 'text-slate-400'}`}>
                Limit: {m.threshold}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="h-[350px] w-full mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            
            {/* 🎯 تفعيل الـ Scale الزمني الصحيح للمحور */}
            <XAxis 
              dataKey="timestamp" 
              type="number" 
              scale="time" 
              domain={['auto', 'auto']}
              tickFormatter={(unixTime) => {
                  const d = new Date(unixTime);
                  return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`;
              }}
              tick={{fontSize: 10, fontWeight: 'bold'}} 
              axisLine={false} 
              tickLine={false} 
              dy={10} 
            />
            
            <YAxis tick={{fontSize: 10}} axisLine={false} tickLine={false} />
            
            <Tooltip 
               labelFormatter={(unixTime) => new Date(unixTime).toLocaleDateString()}
               contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '11px', fontWeight: 'bold' }}
            />
            
            <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 'bold', paddingTop: '15px' }} />
            
            {metersSummary.map((m, idx) => {
              const color = METER_COLORS[idx % METER_COLORS.length];
              return (
                <React.Fragment key={m.meter_num}>
                  {/* 🎯 الإصلاح الثالث: تخفيف شكل خط الحد الأقصى لكي لا يختلط مع خط التوقع */}
                  <ReferenceLine 
                    y={m.threshold} 
                    stroke={color} 
                    strokeDasharray="2 2" 
                    strokeOpacity={0.3}
                    strokeWidth={2} 
                    label={{ position: 'insideTopLeft', value: `Limit ${m.meter_num}`, fill: color, fontSize: 9, opacity: 0.6 }}
                  />
                  <Line type="monotone" dataKey={`${m.meter_num}_hist`} name={`${m.meter_num} (History)`} stroke={color} strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 6 }} connectNulls />
                  
                  {/* إذا كان العداد غير حرج نعرض خط التوقع */}
                  {!m.is_critical && (
                     <Line type="monotone" dataKey={`${m.meter_num}_pred`} name={`${m.meter_num} (AI Forecast)`} stroke={color} strokeWidth={3} strokeDasharray="5 5" dot={false} activeDot={{ r: 6 }} connectNulls />
                  )}
                </React.Fragment>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PredictiveChart;